
import { StarIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useIsMobile } from "@/hooks/use-mobile";

export interface Business {
  id: string;
  title: string;
  category: string;
  description: string;
  contact: string;
  author_name: string;
  author_avatar?: string;
  badges?: string[];
  reviewsAvg?: number;
  reviewsCount?: number;
  image_url?: string;
}

interface BusinessCardProps {
  business: Business;
  onClick: () => void;
  hideImage?: boolean;
}

export const BusinessCard = ({ business, onClick, hideImage = false }: BusinessCardProps) => {
  const isMobile = useIsMobile();
  
  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={onClick}
    >
      {business.image_url && !hideImage && (
        <div className="h-[100px] overflow-hidden">
          <img 
            src={business.image_url} 
            alt={business.title} 
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 overflow-hidden">
            <Avatar className="h-12 w-12 shrink-0">
              <AvatarImage src={business.author_avatar} alt={business.author_name} />
              <AvatarFallback>{business.author_name?.charAt(0) || 'U'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 overflow-hidden">
              <h3 className="text-lg font-bold text-primary truncate max-w-full">{business.title}</h3>
              {/* Removed the author name that was displayed here */}
            </div>
          </div>
          {(business.reviewsAvg !== undefined && business.reviewsAvg > 0) && (
            <div className="flex items-center self-center shrink-0 gap-1">
              <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium">{business.reviewsAvg}</span>
              {business.reviewsCount && business.reviewsCount > 0 && (
                <span className="text-xs text-muted-foreground">({business.reviewsCount})</span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <p className="text-sm line-clamp-3">{business.description}</p>
      </CardContent>
      <CardFooter className="flex flex-wrap gap-2">
        {business.badges && business.badges.map((badge, index) => (
          <Badge key={index} variant="outline" className="text-xs whitespace-nowrap">
            {badge}
          </Badge>
        ))}
      </CardFooter>
    </Card>
  );
};
